import { frameId, type AssetManifest } from "../assets/manifest.ts";
import type { TileRef } from "../document/types.ts";
import type { CataloguePropInfo, EditorCatalogue } from "./catalogue.ts";
import { sheetLabel, tileOf, type FrameFit } from "./frame-fit.ts";
import type { WindowItem } from "./frame-window.ts";
import { packNamesOf } from "./prop-listing.ts";
import { stripAt } from "./strips.ts";
import { frameLabel } from "./transitions.ts";
import type { StripChoice } from "./transitions.ts";

/**
 * What each chooser of the map editor hands the frame and animation window (ADR-107 decision 22): a state's
 * fitting frames, a transition's strips and the palette's frames, as the window's items. A frame that starts a
 * strip is one item holding the strip's frames, its durations and how it plays, so the window plays it and steps
 * through it; any other frame is a still. Each item's art keys are manifest frame ids, and `refs` names the frame
 * each key stands for, a strip's inner frames included, so the chooser can take whichever one the window gives back.
 */

/**
 * A chooser's items and the frame each art key names.
 */
export interface WindowOffer {
  readonly items: readonly WindowItem[];
  readonly refs: ReadonlyMap<string, TileRef>;
}

/**
 * Builds an offer from frames in order, each with its name and heading.
 *
 * @param manifest The editor's asset manifest, for sizes and strips.
 * @param catalogue The world's catalogue, for each frame's group.
 * @param entries The frames, each with its name and the heading it is listed under.
 * @returns The offer; a frame the manifest lacks is left out, and a frame already inside a strip listed before it
 *   is not listed again: the strip carries its name, as the entries name it, else as the inspector names a frame
 *   inside a strip.
 */
function offerOf(manifest: AssetManifest, catalogue: EditorCatalogue, entries: readonly { readonly frame: TileRef; readonly name: string; readonly section: string | null }[]): WindowOffer {
  const groups = new Map(catalogue.props.map((prop) => [frameId(prop.tileset, null, prop.tile), prop.group]));
  const items: WindowItem[] = [];
  const refs = new Map<string, TileRef>();
  const named = new Map(entries.map((entry) => [frameId(entry.frame.tileset, null, entry.frame.tile), entry.name]));
  for (const { frame, name, section } of entries) {
    const key = frameId(frame.tileset, null, frame.tile);
    const rect = manifest.frames[key];
    if (rect === undefined || refs.has(key)) {
      continue;
    }
    const strip = stripAt(manifest, frame);
    const group = groups.get(key);
    const words = group === undefined || group === "" ? [] : [group];
    refs.set(key, { tileset: frame.tileset, tile: frame.tile });
    const frameNames: string[] = [];
    for (const inner of strip === undefined ? [] : strip.frames) {
      const tile = tileOf(inner, frame.tileset);
      if (tile !== undefined) {
        refs.set(inner, tile);
      }
      frameNames.push(inner === key ? name : (named.get(inner) ?? (tile === undefined ? inner : frameLabel(catalogue, manifest, tile))));
    }
    items.push({ key, name, section, words, width: rect.w, height: rect.h, frames: strip === undefined ? [key] : strip.frames, durations: strip === undefined ? [] : strip.durations, plays: strip === undefined ? null : strip.plays, ...(strip === undefined ? {} : { frameNames }) });
  }
  return { items, refs };
}

/**
 * A state's fitting frames as the window's items: the frames nearest the prop under no heading, every other
 * tileset's under its pack and tileset, as the small picker heads them.
 *
 * @param manifest The editor's asset manifest.
 * @param catalogue The world's catalogue.
 * @param fits The frames the picker offers, in its order.
 * @returns The offer.
 */
export function fitItems(manifest: AssetManifest, catalogue: EditorCatalogue, fits: readonly FrameFit[]): WindowOffer {
  const packs = packNamesOf(catalogue);
  return offerOf(
    manifest,
    catalogue,
    fits.map((fit) => ({ frame: fit.frame, name: fit.name, section: fit.sheet === null ? null : sheetLabel(fit.sheet, catalogue, packs) })),
  );
}

/**
 * The strips a transition can play as the window's items, each under its pack and tileset.
 *
 * @param manifest The editor's asset manifest.
 * @param catalogue The world's catalogue.
 * @param choices The strips the chooser offers, in its order.
 * @returns The offer.
 */
export function stripChoiceItems(manifest: AssetManifest, catalogue: EditorCatalogue, choices: readonly StripChoice[]): WindowOffer {
  const packs = packNamesOf(catalogue);
  return offerOf(
    manifest,
    catalogue,
    choices.map((choice) => ({ frame: choice.frame, name: choice.name, section: sheetLabel(choice.frame.tileset, catalogue, packs) })),
  );
}

/**
 * The palette's frames as the window's items, each under its pack and tileset.
 *
 * @param manifest The editor's asset manifest.
 * @param catalogue The world's catalogue.
 * @param props The palette's frames, in its order.
 * @returns The offer.
 */
export function catalogueItems(manifest: AssetManifest, catalogue: EditorCatalogue, props: readonly CataloguePropInfo[]): WindowOffer {
  const packs = packNamesOf(catalogue);
  return offerOf(
    manifest,
    catalogue,
    props.map((prop) => ({ frame: { tileset: prop.tileset, tile: prop.tile }, name: prop.name, section: sheetLabel(prop.tileset, catalogue, packs) })),
  );
}
